import { useEffect, useRef, useState } from 'react';
import { deriveZone, sameZone } from '@repo/front-end-utils';
import type { OnboardingRoute, ZoneDerivation } from '@repo/front-end-utils';
import type { TimezoneSource } from '@repo/frontend-types';

import { isLocated, looksLikePlaceholderOrigin } from './twinDetailsForm';

/**
 * The twin's timezone worked out from its address, and whether the step may write it
 * (`twin-details-page-address`, R4.2–R4.3; ledger rows 23 and 25).
 *
 * `deriveZone` runs on load and after each committed change of country or position. **The newest
 * run wins**: a run started before a later change is discarded, never written (Failure States).
 * The position goes in only when it is usable and not the old 51.4779, 0 placeholder, as
 * `deriveZone`'s own note requires — inside the UK the placeholder lies in London and would be
 * credited *From position*.
 *
 * **Never the browser's zone** (R4.7): nothing here reads the running machine's clock.
 */

export interface ZoneForAddressInput {
  /** The listed code (`countryCodeFor`), or `null`: no country, or text not in the list. */
  countryCode: string | null;
  /** The saved pair, as the form holds it. */
  latitude: string;
  longitude: string;
  /** The twin's saved zone and its source. */
  storedZone: string;
  storedSource: TimezoneSource | null | undefined;
  /** The page's route; `null` until the twin read settles, and then treated as not `create`. */
  route: OnboardingRoute | null;
}

export interface ZoneWrite {
  zone: string;
  source: Extract<TimezoneSource, 'country' | 'position'>;
}

export interface ZoneForAddress {
  /** The latest answer for the current inputs, or `null` while the first run is pending. */
  answer: ZoneDerivation | null;
  /** A run for the current inputs is still working. */
  pending: boolean;
  /** What R4.3 says to write now, or `null`. */
  write: ZoneWrite | null;
  /** A derived zone the address points to that differs from the kept one (R4.3), or `null`. */
  pointsElsewhere: string | null;
}

/** Does a derived answer name a zone other than `stored`? Its other candidates count as the same. */
const namesAnotherZone = (
  answer: Extract<ZoneDerivation, { kind: 'derived' }>,
  stored: string,
): boolean =>
  !sameZone(answer.zone, stored) && !(answer.candidates ?? []).some(z => sameZone(z, stored));

/**
 * R4.3, pure. Writes when the twin has no zone, or when its zone was worked out and it is on the
 * create route (never launched, not live). Otherwise the stored zone is kept, and a derived answer
 * naming another zone is only reported.
 */
export const zoneWrite = (
  answer: ZoneDerivation | null,
  storedZone: string,
  storedSource: TimezoneSource | null | undefined,
  route: OnboardingRoute | null,
): Pick<ZoneForAddress, 'write' | 'pointsElsewhere'> => {
  if (!answer || answer.kind !== 'derived') return { write: null, pointsElsewhere: null };
  const stored = storedZone.trim();
  const write: ZoneWrite = { zone: answer.zone, source: answer.basis };
  if (stored === '') return { write, pointsElsewhere: null };
  if (!namesAnotherZone(answer, stored)) return { write: null, pointsElsewhere: null };
  const workedOut = storedSource === 'country' || storedSource === 'position';
  if (workedOut && route === 'create') return { write, pointsElsewhere: null };
  return { write: null, pointsElsewhere: answer.zone };
};

/** The pair `deriveZone` may use, or `null` (R4.2). */
const usablePosition = (latitude: string, longitude: string) => {
  const pair = { latitude, longitude };
  if (!isLocated(pair) || looksLikePlaceholderOrigin(pair)) return null;
  return { latitude: Number(latitude.trim()), longitude: Number(longitude.trim()) };
};

export const useZoneForAddress = ({
  countryCode,
  latitude,
  longitude,
  storedZone,
  storedSource,
  route,
}: ZoneForAddressInput): ZoneForAddress => {
  const position = usablePosition(latitude, longitude);
  const key = JSON.stringify([countryCode, position]);
  const [settled, setSettled] = useState<{ key: string; answer: ZoneDerivation } | null>(null);
  const genRef = useRef(0);

  useEffect(() => {
    const gen = ++genRef.current;
    const [country, at] = JSON.parse(key) as [
      string | null,
      { latitude: number; longitude: number } | null,
    ];
    void deriveZone({ country, position: at }).then(answer => {
      if (gen === genRef.current) setSettled({ key, answer });
    });
  }, [key]);

  useEffect(
    () => () => {
      genRef.current += 1;
    },
    [],
  );

  // An answer for older inputs is not shown as the current one; the card keeps the stored zone.
  const answer = settled && settled.key === key ? settled.answer : null;
  return {
    answer,
    pending: answer === null,
    ...zoneWrite(answer, storedZone, storedSource, route),
  };
};
